import React, { useState } from 'react';
import { healthAPI } from '../api';
import './QuickEntry.css';

interface SleepFormProps {
    onSuccess: () => void;
    onCancel: () => void;
}

export const SleepForm: React.FC<SleepFormProps> = ({ onSuccess, onCancel }) => {
    // Generate time options for dropdowns
    const generateTimeOptions = () => {
        const times: string[] = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let min of [0, 30]) {
                const value = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
                times.push(value);
            }
        }
        return times;
    };

    const times = generateTimeOptions();
    const [bedTime, setBedTime] = useState('22:00'); // 10pm default
    const [wakeTime, setWakeTime] = useState('06:00'); // 6am default
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Format dates for display
    const formatDate = (daysOffset: number) => {
        const date = new Date();
        date.setDate(date.getDate() + daysOffset);
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
    };

    const formatTimeLabel = (time24: string) => {
        const [hour, min] = time24.split(':').map(Number);
        const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour < 12 ? 'AM' : 'PM';
        return `${h12}:${min.toString().padStart(2, '0')} ${ampm}`;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Calculate duration
        let durationMinutes = undefined;
        if (bedTime && wakeTime) {
            const bedParts = bedTime.split(':').map(Number);
            const wakeParts = wakeTime.split(':').map(Number);
            let bedMinutes = bedParts[0] * 60 + bedParts[1];
            let wakeMinutes = wakeParts[0] * 60 + wakeParts[1];

            // If wake time is earlier than bed time, assume it's the next day
            if (wakeMinutes <= bedMinutes) {
                wakeMinutes += 24 * 60;
            }

            durationMinutes = wakeMinutes - bedMinutes;
        }

        try {
            await healthAPI.createEvent({
                type: 'sleep',
                data: {
                    start_time: bedTime || undefined,
                    end_time: wakeTime || undefined,
                    duration_minutes: durationMinutes,
                }
            });
            onSuccess();
        } catch (err) {
            console.error('Error logging sleep:', err);
            setError('Failed to log sleep');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="quick-entry-form sleep-form" style={{ marginBottom: '16px' }}>
            <h3>🌙 Log Last Night's Sleep</h3>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>
                        {formatDate(-1)}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.85rem', color: '#888', whiteSpace: 'nowrap' }}>Bed:</span>
                        <select
                            value={bedTime}
                            onChange={(e) => setBedTime(e.target.value)}
                            style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                        >
                            {times.map(time => (
                                <option key={time} value={time}>
                                    {formatTimeLabel(time)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>
                        {formatDate(0)}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.85rem', color: '#888', whiteSpace: 'nowrap' }}>Wake:</span>
                        <select
                            value={wakeTime}
                            onChange={(e) => setWakeTime(e.target.value)}
                            style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                        >
                            {times.map(time => (
                                <option key={time} value={time}>
                                    {formatTimeLabel(time)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {error && <div style={{ color: '#ef4444', margin: '8px 0', fontSize: '0.9rem' }}>{error}</div>}

            <button type="submit" disabled={loading} className="submit-button">
                {loading ? 'Logging...' : 'Log Sleep'}
            </button>
        </form>
    );
};
